let fetchSanitizerInstalled = false;
let headersConstructorPatched = false;
let requestConstructorPatched = false;

const NON_LATIN1_REGEX = /[^\u0000-\u00ff]/g;
const HEADER_NAME_REGEX = /[^A-Za-z0-9!#$%&'*+.^_`|~-]/g;
const CRLF_REGEX = /[\r\n]+/g;
const NON_PRINTABLE_ASCII_REGEX = /[^\x20-\x7E]/g;
const AUTH_HEADER_REGEX = /^(authorization|apikey)$/i;

const sanitizeHeaderName = (name: string): string =>
  name.replace(HEADER_NAME_REGEX, "").trim();

const sanitizeHeaderValue = (name: string, value: string): string => {
  const normalized = value.replace(CRLF_REGEX, "");

  if (AUTH_HEADER_REGEX.test(name)) {
    // Keep auth headers strictly printable ASCII so hidden unicode can't break fetch.
    return normalized.replace(NON_PRINTABLE_ASCII_REGEX, "").trim();
  }

  return normalized.replace(NON_LATIN1_REGEX, (char) => encodeURIComponent(char));
};

const isHeadersLike = (headers: HeadersInit): headers is Headers =>
  typeof (headers as Headers)?.forEach === "function";

const isRequestLike = (input: unknown): input is Request =>
  typeof input === "object" &&
  input !== null &&
  "headers" in input &&
  "url" in input &&
  "clone" in input;

const sanitizeHeaders = (headers: HeadersInit): HeadersInit => {
  const safeRecord: Record<string, string> = {};

  if (isHeadersLike(headers)) {
    headers.forEach((value, name) => {
      const safeName = sanitizeHeaderName(name);
      if (!safeName) return;

      safeRecord[safeName] = sanitizeHeaderValue(safeName, String(value));
    });

    return safeRecord;
  }

  if (Array.isArray(headers)) {
    headers.forEach(([name, value]) => {
      const safeName = sanitizeHeaderName(String(name));
      if (!safeName) return;

      safeRecord[safeName] = sanitizeHeaderValue(safeName, String(value));
    });

    return safeRecord;
  }

  Object.entries(headers).forEach(([name, value]) => {
    if (value === undefined || value === null) return;

    const safeName = sanitizeHeaderName(name);
    if (!safeName) return;

    safeRecord[safeName] = sanitizeHeaderValue(safeName, String(value));
  });

  return safeRecord;
};

const sanitizeRequestInit = (init?: RequestInit): RequestInit | undefined => {
  if (!init?.headers) return init;

  return {
    ...init,
    headers: sanitizeHeaders(init.headers),
  };
};

const isHeaderEncodingError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return /headers property|failed to construct 'headers'|iso-8859-1|string contains non iso-8859-1 code point/i.test(message);
};

const patchHeadersConstructor = (): void => {
  if (headersConstructorPatched || typeof window === "undefined" || typeof window.Headers === "undefined") return;

  const NativeHeaders = window.Headers;

  class SafeHeaders extends NativeHeaders {
    constructor(init?: HeadersInit) {
      if (typeof init === "undefined") {
        super();
        return;
      }

      super(sanitizeHeaders(init));
    }

    append(name: string, value: string): void {
      const safeName = sanitizeHeaderName(name);
      if (!safeName) return;

      super.append(safeName, sanitizeHeaderValue(safeName, String(value)));
    }

    set(name: string, value: string): void {
      const safeName = sanitizeHeaderName(name);
      if (!safeName) return;

      super.set(safeName, sanitizeHeaderValue(safeName, String(value)));
    }
  }

  window.Headers = SafeHeaders as typeof Headers;
  headersConstructorPatched = true;
};

const patchRequestConstructor = (): void => {
  if (requestConstructorPatched || typeof window === "undefined" || typeof window.Request === "undefined") return;

  const NativeRequest = window.Request;

  class SafeRequest extends NativeRequest {
    constructor(input: RequestInfo | URL, init?: RequestInit) {
      const normalizedInit = sanitizeRequestInit(init);

      if (input instanceof NativeRequest) {
        const requestWithSafeHeaders: RequestInit = normalizedInit?.headers
          ? normalizedInit
          : {
              ...(normalizedInit ?? {}),
              headers: sanitizeHeaders(input.headers),
            };

        super(input, requestWithSafeHeaders);
        return;
      }

      super(input, normalizedInit);
    }
  }

  window.Request = SafeRequest as typeof Request;
  requestConstructorPatched = true;
};

export const installFetchHeaderSanitizer = (): void => {
  if (fetchSanitizerInstalled || typeof window === "undefined") return;

  patchHeadersConstructor();
  patchRequestConstructor();

  const originalFetch = window.fetch.bind(window);

  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const normalizedInit = sanitizeRequestInit(init);

    try {
      if (isRequestLike(input)) {
        const safeRequest = new Request(input, {
          ...(normalizedInit ?? {}),
          headers: normalizedInit?.headers
            ? normalizedInit.headers
            : sanitizeHeaders(input.headers),
        });

        return await originalFetch(safeRequest);
      }

      return await originalFetch(input, normalizedInit);
    } catch (error) {
      if (!isHeaderEncodingError(error)) throw error;

      if (isRequestLike(input)) {
        const rebuiltRequest = new Request(input, {
          ...(normalizedInit ?? {}),
          headers: sanitizeHeaders(input.headers),
        });

        return originalFetch(rebuiltRequest);
      }

      if (normalizedInit?.headers) {
        return originalFetch(input, {
          ...normalizedInit,
          headers: sanitizeHeaders(normalizedInit.headers),
        });
      }

      throw error;
    }
  }) as typeof window.fetch;

  fetchSanitizerInstalled = true;

  console.info("[FetchSanitizer] Installed — Headers, Request, and fetch patched.");
};

// Install immediately on module evaluation so SDKs that capture fetch/request/headers at init time use sanitized versions.
installFetchHeaderSanitizer();

